import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { PowerBESSLogo } from "../components/PowerBESSLogo";
import "../styles/LegalPage.css";

export default function PrivacyPage() {
  const updated = "May 30, 2026";
  return (
    <div className="lp-legal">
      <Helmet>
        <title>Privacy Policy — PowerBESS.ai</title>
        <meta name="description" content="PowerBESS.ai Privacy Policy under the DPDP Act, 2023 — how we collect, use, and protect data from BESS operators, IPPs, and trading desks across India." />
        <link rel="canonical" href="https://powerbess.ai/privacy" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Privacy Policy — PowerBESS.ai" />
        <meta property="og:description" content="DPDP-aligned privacy policy for PowerBESS.ai customers." />
        <meta property="og:url" content="https://powerbess.ai/privacy" />
      </Helmet>
      <LegalHeader />
      <main className="lp-legal-main">
        <div className="lp-legal-eyebrow">LEGAL · PRIVACY POLICY</div>
        <h1 className="lp-legal-h1">Privacy Policy</h1>
        <p className="lp-legal-sub">Last updated: {updated}</p>

        <section className="lp-legal-sec">
          <p>PowerBESS.ai ("<strong>PowerBESS</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>") respects your privacy. This Privacy Policy explains what personal data we collect, why we collect it, how we use it, with whom we share it, and the rights you have over your information. It is written to align with the Digital Personal Data Protection Act, 2023 ("<strong>DPDP Act</strong>") and applicable Indian and international data-protection standards.</p>
        </section>

        <Section n="1" title="Who We Are">
          <p>PowerBESS is an India-based provider of analytical software and advisory services for grid-scale battery energy storage systems. Where this Policy uses the terms "Data Fiduciary" and "Data Principal," they have the meanings set out in the DPDP Act.</p>
        </Section>

        <Section n="2" title="Information We Collect">
          <h3>2.1 Personal information you provide</h3>
          <ul>
            <li><strong>Contact details:</strong> name, email, work phone, designation, company name.</li>
            <li><strong>Account information:</strong> credentials, role, communication preferences.</li>
            <li><strong>Commercial information:</strong> billing address, GSTIN, purchase records.</li>
            <li><strong>Free-text inputs:</strong> notes you submit in forms, sales calls, or support tickets.</li>
          </ul>
          <h3>2.2 Operational data you submit</h3>
          <ul>
            <li>BESS telemetry (state-of-charge, cycle counts, voltage / temperature where applicable).</li>
            <li>Site metadata (capacity, location, RTE, contracted profile).</li>
            <li>Commercial constraints (PPA terms, schedules, tender obligations, where you share them).</li>
            <li>Trade execution history if integrated through your trading desk or aggregator.</li>
          </ul>
          <h3>2.3 Information collected automatically</h3>
          <ul>
            <li>Usage logs (pages visited, features used, click and session events).</li>
            <li>Device & network metadata (IP address, browser type, OS, time zone).</li>
            <li>Performance and crash diagnostics.</li>
            <li>Cookies & similar technologies — see Section 8 below.</li>
          </ul>
          <h3>2.4 Information from third parties</h3>
          <p>We may receive market data (IEX, public CERC / CEA disclosures, regulator filings) and, where you authorise it, data from your EMS, SCADA, trading desk, or systems integrators.</p>
        </Section>

        <Section n="3" title="How We Use Your Information">
          <p>We process your information only for specified, lawful purposes:</p>
          <ul>
            <li>To provide, operate, secure, and improve the Service.</li>
            <li>To generate dispatch recommendations, run backtests, and produce reports specific to your assets.</li>
            <li>To process billing, communicate service updates, and provide customer support.</li>
            <li>To detect and prevent fraud, security incidents, and abuse of the Service.</li>
            <li>To improve our models in aggregated, anonymised form so that no individual or asset is identifiable.</li>
            <li>To comply with legal, regulatory, audit, and tax obligations.</li>
          </ul>
          <p>We do not use your operational data to train models that are shared with or sold to your competitors.</p>
        </Section>

        <Section n="4" title="Legal Basis for Processing">
          <p>Depending on the data type, we rely on one or more of the following legal bases under the DPDP Act and applicable laws:</p>
          <ul>
            <li><strong>Consent</strong> — for marketing, optional analytics, and any sensitive processing.</li>
            <li><strong>Performance of a contract</strong> — to deliver the Service you have purchased.</li>
            <li><strong>Legitimate uses</strong> — for security, fraud prevention, and improving the Service.</li>
            <li><strong>Compliance with law</strong> — to meet tax, regulatory, and audit obligations.</li>
          </ul>
        </Section>

        <Section n="5" title="Sharing & Disclosure">
          <p>We do not sell your personal data. We share information only in the following circumstances:</p>
          <ul>
            <li><strong>Service providers (Data Processors):</strong> cloud hosting, payment processors, email delivery, analytics, and customer-support tools — bound by data-processing agreements.</li>
            <li><strong>Within your account:</strong> with users and administrators on your subscription, in line with your access controls.</li>
            <li><strong>Regulators and authorities:</strong> where legally required by Indian or applicable foreign law, court order, or to protect against legal liability.</li>
            <li><strong>Corporate transactions:</strong> in connection with a merger, acquisition, or asset transfer; we will notify you and your data will continue to be protected by this Policy or one no less protective.</li>
            <li><strong>With your express consent:</strong> for any other disclosure.</li>
          </ul>
        </Section>

        <Section n="6" title="Data Retention">
          <p>We retain personal and operational data only as long as needed for the purposes set out in this Policy and to meet our legal and regulatory obligations.</p>
          <ul>
            <li><strong>Account & billing records:</strong> retained for the term of your subscription and for up to 8 years after termination to meet tax and audit obligations.</li>
            <li><strong>BESS operational telemetry:</strong> retained for the duration of the engagement and for up to 24 months post-termination, after which it is anonymised or deleted.</li>
            <li><strong>Marketing data:</strong> retained until you withdraw consent or after 24 months of inactivity, whichever is earlier.</li>
          </ul>
        </Section>

        <Section n="7" title="Security">
          <p>We implement industry-standard administrative, physical, and technical safeguards:</p>
          <ul>
            <li>Encryption in transit (TLS 1.2+) and encryption at rest for sensitive data.</li>
            <li>Role-based access controls, least-privilege principles, and audit logging.</li>
            <li>Network segmentation, intrusion detection, and continuous monitoring.</li>
            <li>Regular vulnerability assessments and third-party penetration testing.</li>
            <li>Alignment with CEA Cyber Security in Power Sector guidelines and ISO 27001-aligned controls.</li>
          </ul>
          <p>No security measure is 100% impenetrable. In the unlikely event of a personal data breach, we will notify affected Data Principals and the Data Protection Board of India as required under the DPDP Act.</p>
        </Section>

        <Section n="8" title="Cookies & Similar Technologies">
          <p>We use cookies and similar technologies to keep you signed in, remember preferences, measure performance, and improve our marketing. The categories are:</p>
          <ul>
            <li><strong>Strictly necessary:</strong> required for the Service to function (session, security, accessibility).</li>
            <li><strong>Functional:</strong> remembers your preferences and personalisation.</li>
            <li><strong>Analytics:</strong> understand usage patterns to improve the product.</li>
            <li><strong>Marketing:</strong> measure campaigns and personalise communications.</li>
          </ul>
          <p>You can manage your cookie preferences at any time via our Cookie banner or via your browser settings. Disabling certain cookies may degrade specific features.</p>
        </Section>

        <Section n="9" title="Your Rights">
          <p>Under the DPDP Act and applicable laws, you have the following rights regarding your personal data:</p>
          <ul>
            <li>Right to access — obtain confirmation and copies of your personal data.</li>
            <li>Right to correction & erasure — request correction of inaccurate data or deletion where lawful.</li>
            <li>Right to nominate — appoint a nominee to exercise rights on your behalf upon incapacity.</li>
            <li>Right to grievance redressal — escalate concerns to our grievance officer.</li>
            <li>Right to withdraw consent — at any time, without affecting prior lawful processing.</li>
          </ul>
          <p>To exercise any right, please contact us at <a href="mailto:privacy@powerbess.ai">privacy@powerbess.ai</a>. We will respond within the time limits set by applicable law.</p>
        </Section>

        <Section n="10" title="Cross-Border Transfers">
          <p>Where we transfer your data outside India for processing by cloud or service providers, we ensure such transfers comply with the DPDP Act and use safeguards including standard contractual clauses, data-processing agreements, and reputable service-provider certifications (e.g., SOC 2 Type II, ISO 27001).</p>
        </Section>

        <Section n="11" title="Children's Privacy">
          <p>The Service is intended for use by businesses and is not directed at children under 18. We do not knowingly collect personal data from children.</p>
        </Section>

        <Section n="12" title="Changes to This Policy">
          <p>We may revise this Policy from time to time. Material changes will be notified to you in advance via email or in-platform notice. The "Last updated" date at the top reflects the latest revision.</p>
        </Section>

        <Section n="13" title="Contact & Grievance Officer">
          <p>For any privacy questions, complaints, or rights requests, please reach out to:</p>
          <p><strong>Grievance Officer — PowerBESS.ai</strong><br/>
            Email: <a href="mailto:privacy@powerbess.ai">privacy@powerbess.ai</a><br/>
            Address: To be updated upon registered office finalisation.</p>
          <p>We aim to respond to grievances within 7 working days.</p>
        </Section>

        <div className="lp-legal-footnote">
          <p>This Policy is provided in good faith. It supplements but does not replace any data-protection notice that may apply to a specific contractual relationship with PowerBESS. Please consult your own legal counsel for advice specific to your situation.</p>
        </div>
      </main>
    </div>
  );
}

function LegalHeader() {
  return (
    <header className="lp-legal-header">
      <Link to="/" className="lp-legal-logo">
        <PowerBESSLogo size={26}/>
        <span>Power<span className="lp-legal-a">BESS</span>.ai</span>
      </Link>
      <Link to="/" className="lp-legal-back">
        <ArrowLeft size={14}/> Back to home
      </Link>
    </header>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="lp-legal-sec">
      <h2 className="lp-legal-h2"><span className="lp-legal-h2-n">{n}.</span> {title}</h2>
      <div className="lp-legal-body">{children}</div>
    </section>
  );
}
